/**
 * Backend OperationClaim role names (IntTechService OperationClaimConfiguration seeds).
 * APPROVER is intentionally not defined — it is not a seeded backend claim.
 */
export const OPERATION_CLAIM_ROLES = {
	Superadmin: 'Superadmin',
	Admin: 'Admin',
	User: 'User',
} as const;

export type OperationClaimRole =
	(typeof OPERATION_CLAIM_ROLES)[keyof typeof OPERATION_CLAIM_ROLES];

/** Standard Microsoft JWT role claim type used by IntTechService tokens. */
export const JWT_ROLE_CLAIM =
	'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

const KNOWN_ROLES = new Set<string>(Object.values(OPERATION_CLAIM_ROLES));

export type AuthRoles = {
	roles: string[];
	isSuperadmin: boolean;
	isAdmin: boolean;
	isUser: boolean;
	/** Admin or Superadmin — managers for future Facility/Audit Assignment UI. */
	isManager: boolean;
};

/**
 * Collect role claim values from a decoded JWT payload.
 * jwt-decode returns `string` for one role claim and `string[]` when multiple.
 */
export function collectRoleClaimValues(roleClaim: unknown): string[] {
	if (typeof roleClaim === 'string' && roleClaim.trim()) {
		return [roleClaim.trim()];
	}
	if (Array.isArray(roleClaim)) {
		return roleClaim
			.filter((value): value is string => typeof value === 'string')
			.map((value) => value.trim())
			.filter(Boolean);
	}
	return [];
}

/**
 * Derive known OperationClaim roles from JWT role claim(s) and optional
 * login `permissions` entries that happen to be role names.
 * Unknown permission strings are ignored so feature permissions are not treated as roles.
 */
export function extractKnownRoles(sources: {
	roleClaim?: unknown;
	permissions?: string[] | null;
}): string[] {
	const collected = [
		...collectRoleClaimValues(sources.roleClaim),
		...(sources.permissions ?? []).filter(
			(value): value is string => typeof value === 'string' && value.trim().length > 0
		),
	];

	const unique = new Set<string>();
	for (const name of collected) {
		const trimmed = name.trim();
		if (KNOWN_ROLES.has(trimmed)) {
			unique.add(trimmed);
		}
	}
	return Array.from(unique);
}

export function buildAuthRoles(roles: string[]): AuthRoles {
	const set = new Set(roles);
	const isSuperadmin = set.has(OPERATION_CLAIM_ROLES.Superadmin);
	const isAdmin = set.has(OPERATION_CLAIM_ROLES.Admin);
	const isUser = set.has(OPERATION_CLAIM_ROLES.User);
	return {
		roles,
		isSuperadmin,
		isAdmin,
		isUser,
		isManager: isSuperadmin || isAdmin,
	};
}

export function emptyAuthRoles(): AuthRoles {
	return buildAuthRoles([]);
}
